<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { ref, watch } from "vue";
import { AtomText } from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useDomainLabel } from "~/composables/use-domain-label";
import type { IDomainPickupPackaging } from "~/domain/pickup-packaging";

const css = bemBuilder("packaging-selector");

const props = withDefaults(
  defineProps<{ modelValue?: IDomainPickupPackaging }>(),
  { modelValue: "takeaway" },
);

const emits = defineEmits<{
  "update:modelValue": [value: IDomainPickupPackaging];
}>();

const { t } = useLocale();
const { packagingLabel } = useDomainLabel();

const options: IDomainPickupPackaging[] = ["takeaway", "eatin"];
const selected = ref<IDomainPickupPackaging>(props.modelValue);

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (newValue === oldValue) return;
    selected.value = newValue;
  },
);

function onChange(value: IDomainPickupPackaging) {
  selected.value = value;
  emits("update:modelValue", value);
}
</script>

<template>
  <div :class="css()">
    <AtomText type="subtitle">
      {{ t("components.packaging_selector.title") }}
    </AtomText>
    <div :class="css('list')">
      <label
        v-for="item in options"
        :key="item"
        :data-test="`packaging-item-${item}`"
        :class="[
          css('list-item'),
          { [css('list-item--selected')]: selected === item },
        ]"
      >
        <AtomText
          :color="selected === item ? 'color-on-primary' : 'color-base-20'"
          type="body-bold"
        >
          {{ packagingLabel(item) }}
        </AtomText>
        <input
          type="radio"
          :value="item"
          name="packaging"
          :class="css('list-input')"
          :checked="selected === item"
          @change="onChange(item)"
        />
      </label>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.packaging-selector {
  display: flex;
  gap: var(--spacing-sm);
  flex-direction: column;

  &__list {
    display: flex;
    gap: var(--spacing-sm);

    &-item {
      flex: 1;
      display: flex;
      cursor: pointer;
      align-items: center;
      flex-direction: column;
      justify-content: center;
      padding: var(--spacing-sm);
      border: solid 1px var(--color-base-90);
      border-radius: var(--border-radius-7);

      &--selected {
        border-color: var(--color-primary);
        background-color: var(--color-primary);
      }
    }

    &-input {
      position: absolute;
      opacity: 0;
      height: 0;
      width: 0;
      cursor: pointer;
    }
  }
}
</style>
